import React, { useContext, useMemo, useState } from 'react'
import NextLink from 'next/link'
import { Typography, Chip, Grid, Paper, Link, Box, Rating, useTheme } from '@hermes/web-components'

import { DEFAULT_LOCALE } from '../../../../constants/global'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { AppDataContext } from '../../../../providers/AppData'
import { fixSlug } from '../../../../utils/checkProfileSlugRedirect'
import { PracticeSpecialist } from '../../../../types/practice'
import getImageWithWWW from '../../../../utils/getImageWithWWW'
import Avatar from '../../../Avatar'

type PracticeSpecialistCardProps = {
  specialist: PracticeSpecialist
  selectedKeyword: string
}

function PracticeSpecialistCard({ specialist, selectedKeyword }: PracticeSpecialistCardProps) {
  const theme = useTheme()
  const { locale, language } = useContext(AppDataContext)
  const translate = useTranslateMessage()

  const { subSpecialties, procedures } = React.useMemo(() => {
    const subKeywords = specialist?.keywords?.filter((k) => k.isTopLevelChild) || []
    return {
      subSpecialties: subKeywords.filter((keyword) => keyword.keywordType === 'specialty'),
      procedures: subKeywords.filter((keyword) => keyword.keywordType === 'procedure')
    }
  }, [specialist.keywords])

  const trustValue = ((specialist.bedsideManner + specialist.explanation + specialist.overallExperience) / 3).toFixed(2)
  const { peerRecommendationsCount } = specialist
  const keywordsLength = procedures.length
  const limit = 3

  const [visibleKeywords, setVisibleKeywords] = useState(procedures.slice(0, limit))

  const expandKeywords = () => {
    setVisibleKeywords(procedures)
  }

  const userPrefix = useMemo(
    () => specialist.title?.[language] || specialist.title?.[DEFAULT_LOCALE],
    [language, specialist.title]
  )
  const userFirstName = useMemo(
    () => specialist.firstName?.[language] || specialist.firstName?.[DEFAULT_LOCALE],
    [language, specialist.firstName]
  )
  const userLastName = useMemo(
    () => specialist.lastName?.[language] || specialist.lastName?.[DEFAULT_LOCALE],
    [language, specialist.lastName]
  )

  const userTitle = [userPrefix, userFirstName, userLastName].filter(Boolean).join(' ')
  const userLogoSrc = getImageWithWWW(specialist?.images?.logo)

  return (
    <Box sx={{ display: 'grid', mb: 2, breakInside: 'avoid' }}>
      <Paper elevation={0} sx={{ borderRadius: '8px', width: '100%' }}>
        <Grid container spacing={2} p={2} direction="column">
          <Grid container item spacing={1.5} xs={12}>
            <Grid item>
              <Avatar
                lazy
                width={64}
                height={64}
                style={{ borderRadius: '4px' }}
                src={userLogoSrc}
                alt={userTitle}
                withDoctifyCDNLoader
              >
                <Box
                  width="64px"
                  height="64px"
                  borderRadius="4px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ backgroundColor: '#9faabe' }}
                >
                  <Typography color="white" variant="body2" fontSize={24} textTransform="uppercase">
                    {`${specialist.firstName?.[language][0]}${specialist.lastName?.[language][0]}`}
                  </Typography>
                </Box>
              </Avatar>
            </Grid>
            <Grid container item xs direction="column">
              <Grid item>
                <NextLink
                  prefetch={false}
                  href={`/${locale}/specialist/${fixSlug(specialist.slug)}`}
                  style={{ color: 'inherit', textDecoration: 'inherit' }}
                >
                  <Typography variant="body1" color={theme.custom?.common?.searchIconColor}>
                    {userTitle}
                  </Typography>
                </NextLink>
              </Grid>
              <Grid item>
                <Typography variant="caption" color="text.black900">
                  {subSpecialties?.[0]?.name?.[language] || ''}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: 'background.default',
                width: '100%'
              }}
              elevation={0}
            >
              <Grid container spacing={1}>
                <Grid container item justifyContent="space-between">
                  <Grid item xs={6}>
                    <Typography fontSize={38.49} color="text.darkNavy" component="p" variant="h2" fontWeight={900}>
                      {trustValue}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Rating
                      value={Number(trustValue)}
                      size="small"
                      readOnly
                      sx={{ height: 'fit-content', width: 'fit-content' }}
                    />
                    <Typography variant="subtitle2" component="p" pl={0.5} mt={0.5} fontWeight={400}>
                      {translate('reviews.patient_trust_rating')}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction="row" spacing={1}>
                  <Grid container item xs={6} columnSpacing={1} alignItems="center">
                    <Grid item>
                      <Typography variant="h3" component="p" fontWeight="900" color="text.darkNavy">
                        {specialist.reviewsTotal}
                      </Typography>
                    </Grid>
                    <Grid item xs overflow="hidden">
                      <Typography
                        variant="subtitle2"
                        fontWeight={400}
                        textTransform={'uppercase'}
                        color="text.darkNavy"
                        lineHeight="12px"
                        component="p"
                        sx={{ wordSpacing: '100vw' }}
                      >
                        {translate('patient_reviews', { count: specialist.reviewsTotal })}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container item xs={6} columnSpacing={1} alignItems="center">
                    <Grid item>
                      <Typography variant="h3" component="p" fontWeight={900} color="text.darkNavy">
                        {peerRecommendationsCount}
                      </Typography>
                    </Grid>
                    <Grid item xs overflow="hidden">
                      <Typography
                        variant="subtitle2"
                        fontWeight={400}
                        component="p"
                        textTransform={'uppercase'}
                        color="text.darkNavy"
                        lineHeight="12px"
                        sx={{ wordSpacing: '100vw' }}
                      >
                        {translate('peer_endorsements', { count: peerRecommendationsCount })}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {!!procedures.length && (
            <Grid container item spacing={1} alignItems="center" xs>
              <Grid item>
                <Typography variant="subtitle2" fontWeight={500} sx={{ mr: 1 }} color="text.secondary">
                  {translate('peerRecommendation.recommended_for')}
                </Typography>
              </Grid>
              <Grid container item spacing={1}>
                {visibleKeywords.map((keyword, index) => (
                  <Grid item key={index}>
                    <Chip
                      variant={selectedKeyword === keyword.name[language] ? 'selected' : 'initial'}
                      sx={{ maxWidth: 250 }}
                      label={
                        <Typography variant="caption" fontWeight={500}>
                          {keyword.name[language]}
                        </Typography>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              {keywordsLength > limit && keywordsLength !== visibleKeywords.length && (
                <Grid item>
                  <Link
                    component="button"
                    sx={{ mr: 0.5, my: 0.25, fontSize: '11.85px', fontWeight: 500, cursor: 'pointer' }}
                    onClick={expandKeywords}
                  >
                    {translate('plus_count_more', { count: keywordsLength - limit })}
                  </Link>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  )
}

export default PracticeSpecialistCard
